import React, { useEffect, useMemo, useState } from 'react'
import { fetchProfitLoss } from '../../api'
import { useUser } from '../../contexts/UserContext'
import { Typography, Card, CardContent, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip, Select, MenuItem, Button, Stack } from '@mui/material'
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, Legend, PieChart, Pie, Cell } from 'recharts'
import RefreshIcon from '@mui/icons-material/Refresh'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import DownloadIcon from '@mui/icons-material/Download'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import PaymentsIcon from '@mui/icons-material/Payments'

const PIE_COLORS = ['#2563eb', '#0f766e', '#7c3aed', '#ea580c']

const presets = {
  fy: ['FY-2023', 'FY-2024', 'FY-2025', 'FY-2026'],
  half: ['H1', 'H2'],
  quarter: ['Q1', 'Q2', 'Q3', 'Q4'],
  month: [
    { value: '01', label: 'Jan' }, { value: '02', label: 'Feb' }, { value: '03', label: 'Mar' },
    { value: '04', label: 'Apr' }, { value: '05', label: 'May' }, { value: '06', label: 'Jun' },
    { value: '07', label: 'Jul' }, { value: '08', label: 'Aug' }, { value: '09', label: 'Sep' },
    { value: '10', label: 'Oct' }, { value: '11', label: 'Nov' }, { value: '12', label: 'Dec' },
  ],
}

const today = new Date()
const currentFY = today.getMonth() + 1 >= 4 ? today.getFullYear() : today.getFullYear() - 1

const computeRange = ({ view, fyYear, half, quarter, month, customFrom, customTo }) => {
  const s = new Date(), e = new Date()
  if (view === 'fy') { s.setFullYear(fyYear, 3, 1); e.setFullYear(fyYear + 1, 2, 31) }
  else if (view === 'half') { if (half === 'H1') { s.setFullYear(fyYear, 3, 1); e.setFullYear(fyYear, 8, 30) } else { s.setFullYear(fyYear, 9, 1); e.setFullYear(fyYear + 1, 2, 31) } }
  else if (view === 'quarter') { const m = { Q1: [3, 5], Q2: [6, 8], Q3: [9, 11], Q4: [0, 2] }; const [ms, me] = m[quarter] || [3, 5]; s.setFullYear(ms === 0 ? fyYear + 1 : fyYear, ms, 1); e.setFullYear(me === 2 ? fyYear + 1 : fyYear, me, 31) }
  else if (view === 'month') { const mo = Number(month || today.getMonth() + 1) - 1; const yr = mo <= 2 ? fyYear + 1 : fyYear; s.setFullYear(yr, mo, 1); e.setFullYear(yr, mo + 1, 0) }
  else if (view === 'custom') { return { start: customFrom || today.toISOString().slice(0, 10), end: customTo || today.toISOString().slice(0, 10) } }
  return { start: s.toISOString().slice(0, 10), end: e.toISOString().slice(0, 10) }
}

export default function ProfitLoss() {
  const { user, loading: userLoading } = useUser()
  const [view, setView] = useState('fy')
  const [fyYear, setFyYear] = useState(currentFY)
  const [half, setHalf] = useState('H1')
  const [quarter, setQuarter] = useState('Q1')
  const [month, setMonth] = useState(String(today.getMonth() + 1).padStart(2, '0'))
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)

  const range = useMemo(() => computeRange({ view, fyYear, half, quarter, month, customFrom, customTo }), [view, fyYear, half, quarter, month, customFrom, customTo])

  const fmt = (num) => {
    const v = Number(num || 0)
    if (v === 0) return '₹0.00'
    const [i, d] = v.toFixed(2).split('.')
    const t = i.substring(i.length - 3)
    const o = i.substring(0, i.length - 3)
    return `₹${o ? o.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' : ''}${t}.${d}`
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const pl = await fetchProfitLoss({ start: range.start, end: range.end }).catch(() => null)
      setData(pl?.data || null)
    } catch { setData(null) } finally { setLoading(false) }
  }

  useEffect(() => { fetchData() }, [range.start, range.end])

  const downloadCsv = () => {
    const bd = data?.breakdown || []
    if (!bd.length) return
    const headers = ['name', 'type', 'amount']
    const rows = bd.map(c => headers.map(h => c[h] ?? '').join(','))
    const blob = new Blob([headers.join(',') + '\n' + rows.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = 'profit-loss.csv'
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
  }

  if (userLoading) return <Typography>Loading...</Typography>

  const sm = data?.summary || {}
  const grossProfit = (sm.totalRevenue || 0) - (sm.purchaseTotal || 0)
  const netProfit = sm.netProfit ?? grossProfit - (sm.expensesTotal || 0)
  const margin = sm.profitMargin ?? (sm.totalRevenue > 0 ? (netProfit / sm.totalRevenue) * 100 : 0)
  const timeline = data?.timeline || []
  const breakdown = data?.breakdown || []
  const revenuePie = [
    { name: 'Online Orders', value: sm.onlineRevenue || 0 },
    { name: 'POS Sales', value: sm.posRevenue || 0 },
    { name: 'Sale Invoices', value: sm.saleRevenue || 0 },
  ].filter(d => d.value > 0)

  return (
    <Box className="dashboard-page enterprise-dashboard">
      <Box className="dashboard-hero">
        <Box><Chip label="Financial analytics" size="small" className="dashboard-eyebrow" /><Typography component="h1">Profit & Loss</Typography><Typography>Revenue, costs and profit analysis across all channels.</Typography></Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refresh"><IconButton onClick={fetchData} disabled={loading}><RefreshIcon /></IconButton></Tooltip>
          <Tooltip title="Export CSV"><IconButton onClick={downloadCsv}><DownloadIcon /></IconButton></Tooltip>
        </Box>
      </Box>

      <Card sx={{ mb: 2, border: '1px solid #e2e8f0', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,.04)' }}><CardContent sx={{ py: '8px !important', px: '12px !important' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
            <FilterAltIcon sx={{ fontSize: 16, color: '#64748b' }} />
            {['fy', 'half', 'quarter', 'month', 'custom'].map(v => (
              <Chip key={v} label={v === 'fy' ? 'FY' : v.charAt(0).toUpperCase() + v.slice(1)} size="small" onClick={() => setView(v)} color={view === v ? 'primary' : 'default'} sx={{ height: 24, fontSize: '.62rem', fontWeight: 700 }} />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Select size="small" value={fyYear} onChange={e => setFyYear(Number(e.target.value))} sx={{ height: 28, fontSize: '.7rem', minWidth: 100 }}>
              {presets.fy.map(fy => <MenuItem key={fy} value={Number(fy.replace('FY-', ''))}>{fy}</MenuItem>)}
            </Select>
            {view === 'half' && <Select size="small" value={half} onChange={e => setHalf(e.target.value)} sx={{ height: 28, fontSize: '.7rem', minWidth: 80 }}>{presets.half.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}</Select>}
            {view === 'quarter' && <Select size="small" value={quarter} onChange={e => setQuarter(e.target.value)} sx={{ height: 28, fontSize: '.7rem', minWidth: 90 }}>{presets.quarter.map(q => <MenuItem key={q} value={q}>{q}</MenuItem>)}</Select>}
            {view === 'month' && <Select size="small" value={month} onChange={e => setMonth(e.target.value)} sx={{ height: 28, fontSize: '.7rem', minWidth: 90 }}>{presets.month.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}</Select>}
            {view === 'custom' && <>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ height: 28, fontSize: '.7rem', border: '1px solid #ccc', borderRadius: 4, padding: '0 6px' }} />
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ height: 28, fontSize: '.7rem', border: '1px solid #ccc', borderRadius: 4, padding: '0 6px' }} />
            </>}
            <Button variant="contained" size="small" onClick={fetchData} disabled={loading} sx={{ height: 28, fontSize: '.65rem', textTransform: 'none' }}>Apply</Button>
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: '.58rem' }}>Window: {range.start} to {range.end}</Typography>
      </CardContent></Card>

      {data && <Box className="dashboard-metrics" sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1, mb: 2, overflowX: 'auto', pb: 0.5 }}>
        {[
          { label: 'Total Revenue', value: fmt(sm.totalRevenue), icon: <CurrencyRupeeIcon />, accent: 'blue', trend: `${(sm.onlineCount || 0) + (sm.posCount || 0) + (sm.saleCount || 0)} orders` },
          { label: 'Online Orders', value: fmt(sm.onlineRevenue), icon: <ShoppingCartIcon />, accent: 'teal', trend: `${sm.onlineCount || 0} orders` },
          { label: 'POS Sales', value: fmt(sm.posRevenue), icon: <PointOfSaleIcon />, accent: 'teal', trend: `${sm.posCount || 0} bills` },
          { label: 'Sale Invoices', value: fmt(sm.saleRevenue), icon: <ReceiptLongIcon />, accent: 'blue', trend: `${sm.saleCount || 0} invoices` },
          { label: 'COGS ', value: fmt(sm.purchaseTotal), icon: <LocalShippingIcon />, accent: 'violet', trend: `${sm.purchaseCount || 0} invoices` },
          { label: 'Expenses', value: fmt(sm.expensesTotal), icon: <PaymentsIcon />, accent: 'amber', trend: 'operational' },
          { label: 'Gross Profit', value: fmt(grossProfit), icon: <TrendingUpIcon />, accent: 'green', trend: `${sm.totalRevenue > 0 ? ((grossProfit / sm.totalRevenue) * 100).toFixed(1) : 0}%` },
          { label: 'Net Profit', value: fmt(netProfit), icon: netProfit >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />, accent: netProfit >= 0 ? 'green' : 'red', trend: `${margin.toFixed(1)}% margin` },
        ].map(m => (
          <Card className={`dashboard-metric ${m.accent}`} key={m.label} sx={{ flex: '1 0 155px' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 60, py: '8px !important', px: '10px !important' }}>
              <Box className="dashboard-metric-icon" sx={{ display: 'grid', placeItems: 'center', width: 26, height: 26, color: '#fff', borderRadius: '7px' }}>{m.icon}</Box>
              <Box sx={{ minWidth: 0 }}><Typography sx={{ color: '#64748b', fontSize: '.58rem', fontWeight: 750 }}>{m.label}</Typography><strong style={{ display: 'block', marginTop: 2, color: '#172033', fontSize: '.84rem', fontWeight: 850 }}>{m.value}</strong></Box>
              <Box sx={{ ml: 'auto', alignSelf: 'flex-start', color: '#15803d', fontSize: '.62rem', fontWeight: 800 }}>{m.trend}</Box>
            </CardContent>
          </Card>
        ))}
      </Box>}

      {data && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, mb: 2 }}>
        <Card className="dashboard-revenue-card">
          <CardContent>
            <Box className="dashboard-panel-heading"><Box><Typography variant="h6">Revenue vs COGS vs Expenses</Typography><Typography variant="caption">Monthly comparison</Typography></Box><Chip label={`${timeline.length} months`} size="small" variant="outlined" /></Box>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={timeline} margin={{ top: 15, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} width={46} />
                <ChartTooltip formatter={v => [fmt(v), '']} />
                <Legend />
                <Bar dataKey="revenue" fill="#2563eb" name="Revenue" radius={[4, 4, 0, 0]} />
                <Bar dataKey="cogs" fill="#7c3aed" name="COGS" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" fill="#ea580c" name="Expenses" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="dashboard-health-card">
          <CardContent>
            <Box className="dashboard-panel-heading"><Box><Typography variant="h6">Net Profit Trend</Typography><Typography variant="caption">Monthly net profit after all costs</Typography></Box><TrendingUpIcon color="primary" /></Box>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={timeline} margin={{ top: 15, right: 10, left: 0, bottom: 0 }}>
                <defs><linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#15803d" stopOpacity={0.24} /><stop offset="95%" stopColor="#15803d" stopOpacity={0.02} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} width={46} />
                <ChartTooltip formatter={v => [fmt(v), 'Net']} />
                <Area type="monotone" dataKey="net" stroke="#15803d" fill="url(#netGrad)" strokeWidth={3} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>}

      {data && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, mb: 2 }}>
        <Card className="dashboard-revenue-card">
          <CardContent>
            <Box className="dashboard-panel-heading"><Box><Typography variant="h6">Revenue Sources</Typography><Typography variant="caption">Revenue distribution by channel</Typography></Box></Box>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={revenuePie} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={4} dataKey="value">
                  {revenuePie.map((entry, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <ChartTooltip formatter={(v) => [fmt(v), '']} />
                <Legend verticalAlign="bottom" height={36} formatter={(value) => <span style={{ color: '#475569', fontSize: '.7rem' }}>{value}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="dashboard-health-card">
          <CardContent>
            <Box className="dashboard-panel-heading"><Box><Typography variant="h6">Source Breakdown</Typography><Typography variant="caption">Revenue, COGS and expenses by source</Typography></Box></Box>
            <Box sx={{ maxHeight: 280, overflowY: 'auto', pr: 1 }}>
              {breakdown.length === 0 && <Typography variant="caption" color="text.secondary">No breakdown data available.</Typography>}
              {breakdown.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #f1f5f9' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.type === 'revenue' ? '#2563eb' : item.type === 'cogs' ? '#7c3aed' : '#ea580c' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                    <Chip label={item.type} size="small" sx={{ height: 16, fontSize: '.5rem', bgcolor: item.type === 'revenue' ? '#eff6ff' : item.type === 'cogs' ? '#f5f3ff' : '#fff7ed', color: item.type === 'revenue' ? '#2563eb' : item.type === 'cogs' ? '#7c3aed' : '#ea580c', fontWeight: 700 }} />
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: item.type === 'revenue' ? '#15803d' : item.type === 'cogs' ? '#7c3aed' : '#ea580c' }}>
                    {item.type === 'revenue' ? '+' : '-'}{fmt(item.amount)}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, mt: 1, borderTop: '2px solid #e2e8f0' }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#15803d' }}>Gross Profit</Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#15803d' }}>{fmt(grossProfit)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderTop: '1px solid #e2e8f0' }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: netProfit >= 0 ? '#15803d' : '#dc2626' }}>Net Profit</Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, color: netProfit >= 0 ? '#15803d' : '#dc2626' }}>{fmt(netProfit)} <span style={{ fontSize: '.5rem', color: '#64748b' }}>({margin.toFixed(1)}%)</span></Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>}

      {data && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, mb: 2 }}>
        <Card className="dashboard-revenue-card">
          <CardContent>
            <Box className="dashboard-panel-heading"><Box><Typography variant="h6">Top Selling Products (Online)</Typography><Typography variant="caption">Best performing online products</Typography></Box><ShoppingCartIcon color="primary" /></Box>
            <TableContainer sx={{ maxHeight: 260 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569', textAlign: 'right' }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569', textAlign: 'right' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.topSellingProducts || []).map((p, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '.6rem', color: '#94a3b8' }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', fontWeight: 600 }}>{p.product_name}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', textAlign: 'right' }}>{p.total_qty}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', fontWeight: 700, color: '#15803d', textAlign: 'right' }}>{fmt(p.total_amount)}</TableCell>
                    </TableRow>
                  ))}
                  {(!data.topSellingProducts || data.topSellingProducts.length === 0) && <TableRow><TableCell colSpan={4} align="center"><Typography variant="caption" color="text.secondary">No data available.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
        <Card className="dashboard-health-card">
          <CardContent>
            <Box className="dashboard-panel-heading"><Box><Typography variant="h6">Top POS Products</Typography><Typography variant="caption">Best performing POS products</Typography></Box><PointOfSaleIcon color="primary" /></Box>
            <TableContainer sx={{ maxHeight: 260 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569', textAlign: 'right' }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569', textAlign: 'right' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.topPosProducts || []).map((p, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '.6rem', color: '#94a3b8' }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', fontWeight: 600 }}>{p.product_name}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', textAlign: 'right' }}>{p.total_qty}</TableCell>
                      <TableCell sx={{ fontSize: '.62rem', fontWeight: 700, color: '#15803d', textAlign: 'right' }}>{fmt(p.total_amount)}</TableCell>
                    </TableRow>
                  ))}
                  {(!data.topPosProducts || data.topPosProducts.length === 0) && <TableRow><TableCell colSpan={4} align="center"><Typography variant="caption" color="text.secondary">No data available.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>}

      {data && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, mb: 2 }}>
        <Card className="dashboard-revenue-card">
          <CardContent>
            <Box className="dashboard-panel-heading"><Box><Typography variant="h6">Recent Sale Invoices</Typography><Typography variant="caption">Latest sales transactions</Typography></Box><ReceiptLongIcon color="primary" /></Box>
            <TableContainer sx={{ maxHeight: 260 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569' }}>Invoice</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569' }}>Party</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569', textAlign: 'right' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569', textAlign: 'center' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569' }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.recentSales || []).map((inv, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '.6rem', fontWeight: 600 }}>{inv.invoice_no}</TableCell>
                      <TableCell sx={{ fontSize: '.6rem' }}>{inv.party_name}</TableCell>
                      <TableCell sx={{ fontSize: '.6rem', fontWeight: 700, color: '#15803d', textAlign: 'right' }}>{fmt(inv.final_amount)}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip label={inv.payment_status || 'N/A'} size="small" sx={{ height: 18, fontSize: '.5rem', fontWeight: 700, bgcolor: inv.payment_status === 'paid' ? '#dcfce7' : inv.payment_status === 'unpaid' ? '#fee2e2' : '#fef3c7', color: inv.payment_status === 'paid' ? '#15803d' : inv.payment_status === 'unpaid' ? '#dc2626' : '#d97706' }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '.55rem', color: '#64748b' }}>{inv.sale_date ? new Date(inv.sale_date).toLocaleDateString('en-IN') : '-'}</TableCell>
                    </TableRow>
                  ))}
                  {(!data.recentSales || data.recentSales.length === 0) && <TableRow><TableCell colSpan={5} align="center"><Typography variant="caption" color="text.secondary">No recent sales.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
        <Card className="dashboard-health-card">
          <CardContent>
            <Box className="dashboard-panel-heading"><Box><Typography variant="h6">Recent Purchase Invoices</Typography><Typography variant="caption">Latest purchase transactions</Typography></Box><LocalShippingIcon color="primary" /></Box>
            <TableContainer sx={{ maxHeight: 260 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569' }}>Invoice</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569' }}>Party</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569', textAlign: 'right' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569', textAlign: 'center' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '.62rem', color: '#475569' }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.recentPurchases || []).map((inv, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '.6rem', fontWeight: 600 }}>{inv.invoice_no}</TableCell>
                      <TableCell sx={{ fontSize: '.6rem' }}>{inv.party_name}</TableCell>
                      <TableCell sx={{ fontSize: '.6rem', fontWeight: 700, color: '#dc2626', textAlign: 'right' }}>{fmt(inv.final_amount)}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip label={inv.payment_status || 'N/A'} size="small" sx={{ height: 18, fontSize: '.5rem', fontWeight: 700, bgcolor: inv.payment_status === 'paid' ? '#dcfce7' : inv.payment_status === 'unpaid' ? '#fee2e2' : '#fef3c7', color: inv.payment_status === 'paid' ? '#15803d' : inv.payment_status === 'unpaid' ? '#dc2626' : '#d97706' }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '.55rem', color: '#64748b' }}>{inv.purchase_date ? new Date(inv.purchase_date).toLocaleDateString('en-IN') : '-'}</TableCell>
                    </TableRow>
                  ))}
                  {(!data.recentPurchases || data.recentPurchases.length === 0) && <TableRow><TableCell colSpan={5} align="center"><Typography variant="caption" color="text.secondary">No recent purchases.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>}
    </Box>
  )
}
